import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ResultsDisplay } from './JointApplicationForm';
import { JointApplicationResponse } from './types';
import { apiFetch } from './lib/api';

interface LoanApplicationDetailProps {
	baseUrl: string;
}

const LoanApplicationDetail: React.FC<LoanApplicationDetailProps> = ({ baseUrl }) => {
	const { applicationId } = useParams<{ applicationId: string }>();
	const navigate = useNavigate();
	const [result, setResult] = useState<JointApplicationResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState('');

	const fetchApplication = useCallback(async () => {
		if (!applicationId) return;

		setIsLoading(true);
		setError('');
		try {
			const response = await apiFetch(`${baseUrl}/api/v1/application/get-application/${applicationId}`);
			if (!response.ok) {
				throw new Error(`Failed to fetch application: ${response.status}`);
			}
			const data = await response.json();
			if (data.success && data.data) {
				setResult(data.data as JointApplicationResponse);
			} else {
				throw new Error(data.message || 'Failed to fetch application details');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'An error occurred');
		} finally {
			setIsLoading(false);
		}
	}, [baseUrl, applicationId]);

	useEffect(() => {
		fetchApplication();
	}, [fetchApplication]);

	return (
		<div className="space-y-4">
			{/* Back button + header */}
			<div className="bg-white rounded-lg shadow-sm border p-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<button
							onClick={() => navigate('/loan-applications')}
							className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 border border-gray-300 transition-colors">
							&larr; Back to List
						</button>
						<h3 className="text-lg font-semibold text-gray-900">Application Details</h3>
						{applicationId && (
							<span className="font-mono text-sm text-blue-600 bg-blue-50 px-2 py-0.5 rounded" title={applicationId}>
								{applicationId}
							</span>
						)}
					</div>
					<button
						onClick={fetchApplication}
						disabled={isLoading}
						className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors">
						{isLoading ? 'Loading...' : 'Refresh'}
					</button>
				</div>
			</div>

			{/* Error */}
			{error && (
				<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
					<strong>Error:</strong> {error}
				</div>
			)}

			{/* Loading */}
			{isLoading && !result && (
				<div className="bg-white rounded-lg shadow-sm border p-12 flex flex-col items-center gap-3">
					<svg
						className="animate-spin h-8 w-8 text-blue-600"
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24">
						<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						/>
					</svg>
					<span className="text-gray-500">Loading application details...</span>
				</div>
			)}

			{/* Results */}
			{result && (
				<div>
					<ResultsDisplay result={result} baseUrl={baseUrl} />

					{/* Raw JSON */}
					<div className="mt-6">
						<details className="bg-gray-100 rounded-lg p-4">
							<summary className="cursor-pointer font-medium text-gray-700">View Raw JSON Response</summary>
							<pre className="mt-4 text-xs overflow-auto max-h-96 bg-white p-4 rounded border">
								{JSON.stringify(result, null, 2)}
							</pre>
						</details>
					</div>
				</div>
			)}
		</div>
	);
};

export default LoanApplicationDetail;
